/**
 * Enhanced device utility functions with performance optimizations
 */
import { debounce } from "./performance.js";

// Cache for device detection to avoid repeated calculations
let cachedDeviceType = null;
let lastCheckTime = 0;
const CACHE_DURATION = 1000; // 1 second

export const isMobileDevice = () => {
  return window.innerWidth <= 768;
};

export const isTabletDevice = () => {
  return window.innerWidth > 768 && window.innerWidth <= 1024;
};

export const isDesktopDevice = () => {
  return window.innerWidth > 1024;
};

export const getDeviceType = () => {
  const now = Date.now();

  // Return cached result if still valid
  if (cachedDeviceType && now - lastCheckTime < CACHE_DURATION) {
    return cachedDeviceType;
  }

  // Determine device type
  if (isMobileDevice()) {
    cachedDeviceType = "mobile";
  } else if (isTabletDevice()) {
    cachedDeviceType = "tablet";
  } else {
    cachedDeviceType = "desktop";
  }

  lastCheckTime = now;
  return cachedDeviceType;
};

export const invalidateDeviceCache = () => {
  cachedDeviceType = null;
  lastCheckTime = 0;
};

// Enhanced device detection with user agent fallback
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const deviceType = getDeviceType();

  return {
    type: deviceType,
    isMobile: deviceType === "mobile",
    isTablet: deviceType === "tablet",
    isDesktop: deviceType === "desktop",
    isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
    isIOS: /iphone|ipad|ipod/.test(userAgent),
    isAndroid: /android/.test(userAgent),
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
  };
};

// Setup resize listener with debouncing
const debouncedCacheInvalidation = debounce(invalidateDeviceCache, 100);
window.addEventListener("resize", debouncedCacheInvalidation);

// Backward compatibility
export { isMobileDevice as default };
